import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { Typography, Box, IconButton, Grid, alpha, Paper, Tabs, Tab, useMediaQuery, Button } from '@mui/material';
import { Close as CloseIcon, TrendingUp, TrendingDown } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import modifyScriptName from '../../../../utils/modifyScriptName';
import axiosInstance from '../../../../utils/axios';
import {
  getUserPortfolio,
  cancelTrade,
  startTradeLoading,
  getTradeHistory,
  stopTradeLoading,
} from '../../../../redux/slices/trades';
import { Wrapper } from '../watchlist/BuyScriptDrawer'; // Assuming Wrapper is correctly exported
import { RHFTextField, FormProvider, RHFToggleButtonGroup, RHFCheckbox } from '../../../../components/hook-form/index';
import useAuth from '../../../../hooks/useAuth';
import { fNumber } from '../../../../utils/formatNumber';

// Shared style objects to reduce duplication
const sharedStyles = {
  quantityButton: (theme) => ({
    border: '1px solid',
    borderColor: alpha(theme.palette.primary.main, 0.3),
    borderRadius: 1,
    width: 36,
    height: 36,
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    color: 'primary.main',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      bgcolor: alpha(theme.palette.primary.main, 0.12),
      borderColor: 'primary.main',
      transform: 'translateY(-1px)',
      boxShadow: '0 2px 8px 0 rgba(0,0,0,0.1)',
    },
    '&:active': {
      transform: 'translateY(0)',
    },
    touchAction: 'manipulation',
  }),
  numberInput: (theme) => ({
    '& .MuiOutlinedInput-root': {
      height: 52,
      borderRadius: 1.5,
      backgroundColor: alpha(theme.palette.background.paper, 0.8),
      boxShadow: '0 1px 4px 0 rgba(0,0,0,0.05)',
      transition: 'all 0.2s ease-in-out',
      '& input': {
        textAlign: 'center',
        fontSize: 20,
        fontWeight: 600,
        fontFamily: 'Roboto Mono, monospace',
        letterSpacing: 1,
        color: 'text.primary',
        paddingX: 1,
        WebkitAppearance: 'none',
        MozAppearance: 'textfield',
        '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': {
          WebkitAppearance: 'none',
          margin: 0,
        },
      },
      '& fieldset': {
        borderColor: alpha(theme.palette.primary.main, 0.2),
        borderWidth: '1px',
      },
      '&:hover': {
        boxShadow: '0 2px 8px 0 rgba(0,0,0,0.1)',
      },
      '&:hover fieldset': {
        borderColor: 'primary.main',
        borderWidth: '1px',
      },
      '&.Mui-focused': {
        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
      },
      '&.Mui-focused fieldset': {
        borderColor: 'primary.main',
        borderWidth: '1px',
      },
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.75rem',
      fontWeight: 700,
      color: 'text.secondary',
      letterSpacing: '0.5px',
    },
    '& .MuiInputLabel-shrink': {
      transform: 'translate(14px, -9px) scale(0.75)',
      backgroundColor: 'background.paper',
      paddingX: 0.5,
    },
  }), actionButton: (color) => {    // Define our vibrant trading button colors
    const getTradeColor = (colorName, variant) => {
      if (colorName === 'success') {
        // Bright green for BUY
        if (variant === 'main') return '#11C15B';
        if (variant === 'dark') return '#0CA54C';
        return '#E6F9F0';
      }
      if (colorName === 'error') {
        // Bright red for SELL
        if (variant === 'main') return '#FF4842';
        if (variant === 'dark') return '#C83A34';
        return '#FFE8E5';
      }
      // Default MUI colors for other buttons
      return `${colorName}.${variant}`;
    };

    return {
      height: 56,
      bgcolor: getTradeColor(color, 'main'),
      color: 'common.white',
      borderRadius: 0,
      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
      transition: 'all 0.3s ease',
      '&:hover': {
        bgcolor: getTradeColor(color, 'dark'),
        boxShadow: '0 6px 12px rgba(0,0,0,0.2)',
        transform: 'translateY(-2px)',
      },
      '&:active': {
        transform: 'translateY(0)',
        boxShadow: '0 3px 6px rgba(0,0,0,0.1)',
      },
      '&.Mui-disabled': {
        opacity: 0.6,
        bgcolor: theme => theme.palette.grey[400],
      },
      textTransform: 'uppercase',
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5,
      letterSpacing: '0.5px',
    };
  }
};

// Custom hook to handle quantity increment/decrement with proper memoization
function useQuantityHandlers({ setValue, getValues, setQuantityState }) {
  const incrementHandler = useCallback((e) => {
    e.preventDefault();
    const currentValue = parseInt(getValues('quantity') || 1, 10);
    const newValue = currentValue + 1;
    setValue('quantity', newValue, { shouldValidate: false, shouldDirty: true, shouldTouch: true });
    setQuantityState(newValue);
  }, [getValues, setValue, setQuantityState]);

  const decrementHandler = useCallback((e) => {
    e.preventDefault();
    const currentValue = parseInt(getValues('quantity') || 1, 10);
    const newValue = Math.max(1, currentValue - 1);
    setValue('quantity', newValue, { shouldValidate: false, shouldDirty: true, shouldTouch: true });
    setQuantityState(newValue);
  }, [getValues, setValue, setQuantityState]);

  return { incrementHandler, decrementHandler };
}

// Format quote values in a consistent manner with styled components
const QuoteValueItem = React.memo(({ label, value }) => {
  const theme = useTheme();

  // Format numerical values to always have 2 decimal places for consistency
  const formattedValue = typeof value === 'number'
    ? value.toFixed(2)
    : (value || '0.00');

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 0.5,
        px: 1,
        borderRadius: 1,
        '&:hover': {
          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
        },
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          fontWeight: 500,
          fontSize: '0.8125rem',
          flexBasis: '40%',
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 600,
          fontFamily: 'Roboto Mono, monospace',
          color: 'text.primary',
          flexBasis: '60%',
          textAlign: 'right',
          letterSpacing: '0.5px',
          minWidth: '70px',
        }}
      >
        {formattedValue}
      </Typography>
    </Box>
  );
});

// Add PropTypes validation for QuoteValueItem
QuoteValueItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

const CloseTradeModal = ({
  trade,
  isOpen,
  onClose,
  closeFromTrade = false,
  closeFromPortfolio = false,
  onUpdateSuccess,
  isManualTrade,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isLoading = useSelector((state) => state?.trade?.isLoading);
  const quote = useSelector((state) => state?.trade?.quotesData[trade?.script?.instrument_token]);
  const [scriptQuote, setScriptQuote] = useState(null); // For storing direct API quote data
  const { user } = useAuth(); const [activeTab, setActiveTab] = useState(0); // 0: MARKET, 1: LIMIT, 2: STOP LIMIT
  const [quantityState, setQuantityState] = useState(trade?.quantity || 1); // Track quantity in component state
  // Create refs to track if we've set initial prices
  const limitPriceSetRef = useRef(false);
  const stopPriceSetRef = useRef(false);
  const modifyPriceSetRef = useRef(false);

  // Determine the initial action type based on trade type
  const initialActionType = trade.type === 'OPEN' ? 'close' : 'cancel';
  // Memoize the schema to prevent unnecessary rebuilds
  const schema = useMemo(() => yup.object().shape({
    quantity: yup
      .number()
      .required('Quantity is required')
      .typeError('Quantity must be a number')
      .positive('Quantity must be greater than zero'),
    // Removed the max quantity validation to allow unlimited values
    orderType: yup.string().required('Order type is required').oneOf(['MARKET', 'LIMIT', 'STOP_LIMIT']),
    limit: yup
      .number()
      .typeError('Limit price must be a number')
      .positive('Limit price must be greater than zero')
      .when('orderType', {
        is: 'LIMIT',
        then: yup.number().required('Limit price is required'),
        otherwise: yup.number().notRequired(),
      }),
    stopPrice: yup
      .number()
      .typeError('Limit price must be a number')
      // .min(0.01, 'Limit price must be greater than zero')
      .when('orderType', {
        is: 'STOP_LIMIT',
        then: yup.number().required('Limit price is required'),
        otherwise: yup.number().notRequired(),
      }),
    tradeAction: yup.string().required('Action is required'),
    useCloseAll: yup.boolean(),
  }), []);

  const methods = useForm({
    mode: 'onChange', // Validate on change to provide immediate feedback
    reValidateMode: 'onChange', // Re-validate when the user makes changes
    resolver: yupResolver(schema),
    defaultValues: {
      quantity: trade.quantity,
      orderType: 'MARKET', // Default to MARKET tab
      tradeAction: initialActionType, // 'close' for OPEN, 'cancel' for PENDING
      limit: trade.side === 'BUY' ? trade.buy : trade.sell, // Initial price suggestion for LIMIT
      stopPrice: 0, // Initial stop price for STOP_LIMIT
      useCloseAll: false,
    },
  });
  const { handleSubmit, reset, watch, setValue, getValues, trigger, formState } = methods; const { errors, isValid, isDirty } = formState;
  const selectedOption = watch('tradeAction'); // For PENDING: 'cancel' or 'modify'
  const orderType = watch('orderType'); // MARKET, LIMIT, or STOP_LIMIT
  const useCloseAll = watch('useCloseAll'); // Whether to close all quantity  

  // Use our custom hook for quantity handlers
  const { incrementHandler, decrementHandler } = useQuantityHandlers({
    setValue,
    getValues,
    setQuantityState
  });

  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  // Memoize common options arrays to prevent unnecessary re-renders
  const stopLimitOptions = useMemo(() => [
    { label: 'STOP LOSS', value: 'sl' },
    { label: 'TARGET', value: 'target' },
  ], []);

  const pendingActionOptions = useMemo(() => [
    { label: 'Cancel', value: 'cancel' },
    { label: 'Modify', value: 'modify' },
  ], []);
  // No longer need memoized button labels as we're determining them directly in the JSX

  // Add trade.type to dependency array for schema reactivity
  const tradeType = trade.type;

  // Update activeTab when orderType changes
  useEffect(() => {
    if (trade.type !== 'PENDING') {
      if (orderType === 'MARKET') setActiveTab(0);
      if (orderType === 'LIMIT') setActiveTab(1);
      if (orderType === 'STOP_LIMIT') setActiveTab(2);
    }
  }, [orderType, trade.type]);

  // Update orderType when activeTab changes
  useEffect(() => {
    if (trade.type !== 'PENDING') {
      if (activeTab === 0) setValue('orderType', 'MARKET');
      if (activeTab === 1) setValue('orderType', 'LIMIT');
      if (activeTab === 2) setValue('orderType', 'STOP_LIMIT');
      trigger();
    }
  }, [activeTab, setValue, trigger, trade.type]);
  // Reset limitPriceSetRef when activeTab changes to ensure price updates properly on tab switch
  useEffect(() => {
    if (activeTab === 1) { // When switching to LIMIT tab
      limitPriceSetRef.current = false; // Reset the ref to allow initial price setting
    } else if (activeTab === 2) { // When switching to STOP LIMIT tab
      stopPriceSetRef.current = false; // Reset the ref to allow initial price setting
    }
  }, [activeTab]);

  useEffect(() => {
    trigger(); // Re-trigger validation when trade type or quote changes
  }, [tradeType, trigger, quote]);  // Fetch quote data when modal opens - optimized with memoized fetch function
  // Memoize the fetch function to avoid recreating it on each render
  const fetchScriptQuote = useCallback(async () => {
    if (!isOpen || !trade?.script?._id) return;

    try {
      const response = await axiosInstance.get(`/script/quote?scriptId=${trade.script._id}`);

      if (response?.data?.data) {
        const quoteData = Object.values(response.data.data)[0];
        setScriptQuote(quoteData);
      }
    } catch (err) {
      // Log the error in development environment only
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching script quote:', err);
      }
      // Don't show error to user as this is a background operation
    }
  }, [isOpen, trade?.script?._id]);

  // Only fetch quote data once when modal opens
  useEffect(() => {
    if (isOpen && trade?.script?._id) {
      fetchScriptQuote();
    }
  }, [isOpen, trade?.script?._id, fetchScriptQuote]);

  // Memoize the default values for form reset to prevent unnecessary calculations
  const getDefaultValues = useMemo(() => () => {
    const initialQuantity = trade.quantity || 1;

    // Get initial price based on trade side
    const initialPrice = parseFloat(parseFloat(trade.side === 'BUY' ? trade.buy : trade.sell).toFixed(2));

    return {
      quantity: initialQuantity,
      orderType: 'MARKET',
      tradeAction: trade.type === 'OPEN' ? 'close' : 'cancel',
      limit: initialPrice,
      stopPrice: initialPrice, // Use the same price for stopPrice
      useCloseAll: false,
    };
  }, [trade.type, trade.quantity, trade.buy, trade.sell, trade.side]);

  useEffect(() => {
    // Reset the form with appropriate defaults when the modal opens or the trade changes
    if (isOpen) {
      const defaults = getDefaultValues(); reset(defaults);

      // Also update our component state to keep in sync
      setQuantityState(defaults.quantity);
      setActiveTab(0); // Reset to MARKET tab

      // Validate the form after resetting to ensure button state is correct
      // Use requestAnimationFrame instead of setTimeout for better performance
      requestAnimationFrame(() => {
        trigger();
      });
    }
  }, [isOpen, getDefaultValues, reset, trigger]);

  useEffect(() => {
    // Only update prices when order type changes or modal opens, not on every quote update
    if (quote && quote.depth && isOpen) {
      if (orderType === 'LIMIT' && !limitPriceSetRef.current) {
        // For LIMIT orders, use bid/ask price as initial value only once
        if (trade.side === 'BUY') {
          // For BUY trades closing, use the bid price
          const bidPrice = quote.depth.buy?.[0]?.price || 0;
          setValue('limit', parseFloat(bidPrice.toFixed(2)));
        } else {
          // For SELL trades closing, use the ask price
          const askPrice = quote.depth.sell?.[0]?.price || 0; setValue('limit', parseFloat(askPrice.toFixed(2)));
        }
        // Mark that we've set the initial price
        limitPriceSetRef.current = true;
      } else if (orderType === 'STOP_LIMIT' && !stopPriceSetRef.current) {
        // For STOP_LIMIT orders, use the same price initialization logic as LIMIT
        if (trade.side === 'BUY') {
          // For BUY trades closing, use the bid price
          const bidPrice = quote.depth.buy?.[0]?.price || 0;
          setValue('stopPrice', parseFloat(bidPrice.toFixed(2)));
        } else {
          // For SELL trades closing, use the ask price
          const askPrice = quote.depth.sell?.[0]?.price || 0;
          setValue('stopPrice', parseFloat(askPrice.toFixed(2)));
        }
        // Mark that we've set the initial price
        stopPriceSetRef.current = true;
      }
    }
  }, [orderType, isOpen, setValue, trade.side, quote]);
  // Reset the refs when modal opens/closes or when action type changes  
  useEffect(() => {
    if (!isOpen) {
      modifyPriceSetRef.current = false;
      limitPriceSetRef.current = false; // Reset limit price ref when modal closes
      stopPriceSetRef.current = false; // Reset stop price ref when modal closes
      // We're now using simple click handlers
    }
  }, [isOpen]);

  // Reset the modify price ref when the selectedOption changes
  useEffect(() => {
    modifyPriceSetRef.current = false;
  }, [selectedOption]);
  // No longer need to clean up intervals

  useEffect(() => {
    // When we have live quotes and relevant selections
    if (quote && quote.depth && isOpen) {
      // Special handling for PENDING 'modify' - set price only once
      if (trade.type === 'PENDING' && selectedOption === 'modify' && !modifyPriceSetRef.current) {
        // For modify, use appropriate price based on depth data
        if (quote.depth.buy && quote.depth.buy[0]) {
          setValue('limit', quote.depth.buy[0].price);
          modifyPriceSetRef.current = true; // Mark that we've set the price
        }
      }
    }
  }, [quote, trade.type, trade.side, selectedOption, isOpen, setValue]);

  // Using useMemo for handlers and related values to prevent unnecessary re-renders
  const handleFinalize = useMemo(() => (isSuccess = true) => {
    if (isSuccess) {
      if (closeFromPortfolio && !isManualTrade) {
        dispatch(getUserPortfolio());
      }
      if (closeFromTrade && !isManualTrade) {
        dispatch(getTradeHistory());
      }
      if (isManualTrade) {
        onUpdateSuccess?.();
      }
    }
    if (isManualTrade || !isSuccess) { // Stop loading for manual trades or on error
      dispatch(stopTradeLoading());
    }
    onClose();
  }, [closeFromPortfolio, closeFromTrade, dispatch, isManualTrade, onClose, onUpdateSuccess]);  // handleFinalize is now defined as a memoized function above
  const handleTradeSubmit = useMemo(() => async (data, side) => {
    // Extra validation for SL/Target - check if the limit price is 0
    if (activeTab === 2 && (!data.stopPrice || parseFloat(data.stopPrice) <= 0)) {
      enqueueSnackbar('Limit price must be greater than zero', { variant: 'error' });
      return;
    }

    dispatch(startTradeLoading());
    try {
      const payload = { ...data };
      // Use the provided side directly (BUY or SELL)
      payload.side = side;
      payload.scriptId = trade.script._id;

      // Set orderType based on active tab
      if (activeTab === 0) {
        payload.orderType = 'MARKET';
        delete payload.limit;
        delete payload.stopPrice;
      } else if (activeTab === 1) {
        payload.orderType = 'LIMIT';
        // Keep limit as-is instead of mapping to price
        delete payload.stopPrice;
      } else if (activeTab === 2) {
        // Send 'LIMIT' instead of 'STOP_LIMIT' to the backend
        payload.orderType = 'LIMIT';
        payload.triggerPrice = payload.stopPrice;
        payload.limit = payload.stopPrice; // Use the stopPrice value for limit
        delete payload.stopPrice;
      }      // Remove fields not needed for API
      delete payload.tradeAction;
      delete payload.stopPrice;      delete payload.useCloseAll; // Remove this field if it exists

      // Check if we're closing position (opposite side) and quantity is less than trade quantity
      // Only apply partial close logic for MARKET orders (activeTab === 0) and NOT from portfolio
      const isClosingPosition = (trade.side === 'BUY' && side === 'SELL') || (trade.side === 'SELL' && side === 'BUY');
      
      // Add cancelFromPortfolio flag only when closing from portfolio AND actually closing position (opposite side)
      if (closeFromPortfolio && isClosingPosition) {
        payload.cancelFromPortfolio = true;
      }

      const isPartialClose = activeTab === 0 && isClosingPosition && parseInt(data.quantity, 10) <= trade.quantity && !closeFromPortfolio;

      let response;
      if (isPartialClose) {
        // Use trade-close API for partial closing (MARKET orders only and NOT from portfolio)
        response = await axiosInstance.put(`/trade/close-trade/${trade._id}`, payload);
      } else {
        // Use trade/add API for new positions, quantities greater than trade quantity, non-MARKET orders, or from portfolio
        response = await axiosInstance.post('/trade/add', payload);
      }

      enqueueSnackbar(response.data.message);
      if (response.data.tradesCancelled) {
        enqueueSnackbar(response.data.cancelMessage, { persist: true });
      }
      handleFinalize(true);
    } catch (err) {
      enqueueSnackbar(err.message || 'Error processing your request', { variant: 'error' });
      handleFinalize(false);
    }
  }, [activeTab, closeFromPortfolio, closeFromTrade, dispatch, enqueueSnackbar, handleFinalize, trade.script?._id, trade.side, trade.quantity, trade._id, trade.type]);

  const handleCancelTrade = useMemo(() => async () => {
    dispatch(startTradeLoading());
    try {
      const res = await dispatch(cancelTrade({ tradeId: trade._id, isManualTrade }));
      enqueueSnackbar(res.message);
      handleFinalize(true);
    } catch (err) {
      enqueueSnackbar(err.message, { variant: 'error' });
      handleFinalize(false);
    }
  }, [dispatch, enqueueSnackbar, handleFinalize, isManualTrade, trade._id]);

  const handleModifyOrder = useMemo(() => async (data) => {
    dispatch(startTradeLoading());
    try {
      const payload = {
        quantity: data.quantity,
        price: data.limit, // Changed from price to limit
        // stopPrice: data.stopPrice
      };
      const response = await axiosInstance.put(`/trade/modify/limit/${trade._id}`, payload);
      enqueueSnackbar(response.data.message);
      handleFinalize(true);
    } catch (err) {
      enqueueSnackbar(err.message || 'Limit Between Bid/Ask is not allowed', { variant: 'error' });
      handleFinalize(false);
    }
  }, [dispatch, enqueueSnackbar, handleFinalize, trade._id]);

  return (
    <Wrapper
      open={isOpen}
      onClose={onClose}
      isMobile={isMobile}
    >
      <Box
        width={{ xs: '100%', sm: '380px' }}
        height={isMobile ? '100vh' : 'auto'}
        marginX={'auto'}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box
          sx={{
            position: 'relative',
            pb: 1.5,
            pt: isMobile ? 'max(env(safe-area-inset-top), 16px)' : 1.5,
            px: 2,
            bgcolor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <IconButton
            sx={{
              position: 'absolute',
              top: isMobile ? 'max(calc(env(safe-area-inset-top) + 16px), 24px)' : 8,
              right: 8,
              color: 'text.secondary',
              zIndex: 10,
              bgcolor: (theme) => alpha(theme.palette.background.paper, 0.9),
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              '&:hover': {
                bgcolor: (theme) => alpha(theme.palette.grey[500], 0.15),
              },
            }}
            onClick={onClose}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <Typography
            variant="h5"
            textAlign={'center'}
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              letterSpacing: 0.5,
              mt: isMobile ? 'max(calc(env(safe-area-inset-top) + 4px), 16px)' : 0.5,
              px: 5,
            }}
          >
            {modifyScriptName(trade?.script?.tradingsymbol, trade?.script?.expiry)?.toUpperCase()}
          </Typography>
          {trade.type === 'OPEN' && (
            <Paper
              elevation={0}
              sx={{
                mt: 2,
                mb: 2,
                py: 1.5,
                px: 2,
                backgroundColor: (theme) => alpha(
                  trade.side === 'BUY' ? theme.palette.success.main : theme.palette.error.main,
                  0.1
                ),
                borderRadius: 1,
                border: '1px solid',
                borderColor: (theme) => alpha(
                  trade.side === 'BUY' ? theme.palette.success.main : theme.palette.error.main,
                  0.3
                ),
              }}
            >
              <Box
                sx={{
                  py: 0.5,
                  px: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexWrap: 'wrap'
                }}
              >
                <Box component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 500, mr: 0.5 }}>
                  POSITION:
                </Box>
                <Box
                  component="span"
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    color: trade.side === 'BUY' ? 'success.main' : 'error.main',
                    mr: 0.5
                  }}
                >
                  {trade.side === 'BUY' ? 'BOUGHT' : 'SOLD'}
                </Box>
                <Box
                  component="span"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.90rem',
                    color: 'text.primary'
                  }}
                >
                  {trade?.quantity}
                </Box>
                <Box component="span" sx={{ mx: 0.5, color: 'text.secondary' }}>@</Box>
                <Box
                  component="span"
                  sx={{
                    fontFamily: 'Roboto Mono, monospace',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    color: trade.side === 'BUY' ? 'success.main' : 'error.main'
                  }}
                >
                  {trade.side === 'BUY' ? fNumber(trade.buy) : fNumber(trade.sell)}
                </Box>
              </Box>
            </Paper>
          )}

          {/* Tabs for Order Types */}
          {trade.type !== 'PENDING' && (
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              variant="fullWidth"
              sx={{
                mb: 2,
                '& .MuiTabs-indicator': {
                  backgroundColor: 'primary.main',
                },
                '& .MuiTab-root': {
                  fontWeight: 600,
                  textTransform: 'none',
                  minHeight: 48,
                  '&.Mui-selected': {
                    color: 'primary.main',
                  },
                },
              }}
            >
              <Tab label="MARKET" />
              <Tab label="LIMIT" />
              <Tab label="STOP LIMIT" />
            </Tabs>
          )}

          {trade.type === 'OPEN' && (
            <FormProvider methods={methods}>
              <Box sx={{ px: 0, py: 1.5 }}>

                {/* Quantity Field with +/- buttons */}
                <Box
                  className="quantity-control-wrapper"
                  sx={{ position: 'relative', mb: 2 }}
                >
                  <RHFTextField
                    name="quantity"
                    label="QUANTITY"
                    type="number"
                    InputProps={{
                      startAdornment: (
                        <IconButton
                          onMouseDown={decrementHandler}
                          sx={sharedStyles.quantityButton(theme)}
                        >
                          <Box component="span" sx={{ fontSize: '1.25rem', fontWeight: 'bold', lineHeight: 0 }}>−</Box>
                        </IconButton>
                      ),
                      endAdornment: (
                        <IconButton
                          onMouseDown={incrementHandler}
                          sx={sharedStyles.quantityButton(theme)}
                        >
                          <Box component="span" sx={{ fontSize: '1.25rem', fontWeight: 'bold', lineHeight: 0 }}>+</Box>
                        </IconButton>
                      ),
                    }}
                    sx={sharedStyles.numberInput(theme)}
                  />
                </Box>

                {/* LIMIT Order Price Field */}
                {orderType === 'LIMIT' && (
                  <Box sx={{ mb: 2 }}>
                    <RHFTextField
                      name="limit"
                      label="LIMIT PRICE"
                      type="number"
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          height: 52,
                          borderRadius: 1.5,
                          backgroundColor: (theme) => alpha(theme.palette.background.neutral, 0.9),
                          boxShadow: '0 1px 4px 0 rgba(0,0,0,0.05)',
                          transition: 'all 0.2s ease-in-out',
                          '& input': {
                            textAlign: 'center',
                            fontSize: 20,
                            fontWeight: 600,
                            fontFamily: 'Roboto Mono, monospace',
                            letterSpacing: 1,
                            color: 'text.primary',
                            paddingX: 1,
                            WebkitAppearance: 'none',
                            MozAppearance: 'textfield',
                            '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': {
                              WebkitAppearance: 'none',
                              margin: 0,
                            },
                          },
                        },
                      }}
                    />
                  </Box>
                )}                {/* STOP LIMIT Fields */}
                {orderType === 'STOP_LIMIT' && (
                  <Box sx={{ mb: 2, display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
                    <RHFTextField
                      name="stopPrice"
                      label="LIMIT PRICE"
                      type="number"
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          height: 52,
                          borderRadius: 1.5,
                          backgroundColor: (theme) => alpha(theme.palette.background.neutral, 0.9),
                          boxShadow: '0 1px 4px 0 rgba(0,0,0,0.05)',
                          transition: 'all 0.2s ease-in-out',
                          '& input': {
                            textAlign: 'center',
                            fontSize: 20,
                            fontWeight: 600,
                            fontFamily: 'Roboto Mono, monospace',
                            letterSpacing: 1,
                            color: 'text.primary',
                            paddingX: 1,
                            WebkitAppearance: 'none',
                            MozAppearance: 'textfield',
                            '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': {
                              WebkitAppearance: 'none',
                              margin: 0,
                            },
                          },
                          '& fieldset': {
                            borderColor: alpha(theme.palette.primary.main, 0.2),
                            borderWidth: '1px',
                          },
                          '&:hover': {
                            boxShadow: '0 2px 8px 0 rgba(0,0,0,0.1)',
                          },
                          '&:hover fieldset': {
                            borderColor: 'primary.main',
                            borderWidth: '1px',
                          },
                          '&.Mui-focused': {
                            boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: 'primary.main',
                            borderWidth: '1px',
                          },
                        },
                      }}
                    />
                  </Box>
                )}

                {/* Action Buttons */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, my: 2 }}>
                  {/* BUY Button */}
                  <LoadingButton
                    onClick={handleSubmit((data) => handleTradeSubmit(data, 'BUY'))}
                    data-side="BUY"
                    sx={{
                      ...sharedStyles.actionButton('success'),
                      borderRadius: 0,
                    }}
                    loading={isLoading}
                    disabled={!isValid}
                  ><Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center', width: '100%' }}>
                      <TrendingUp fontSize="small" />
                      <Typography variant="button" sx={{ fontWeight: 600 }}>
                        {(() => {
                          switch (orderType) {
                            case 'MARKET': return 'BUY';
                            case 'LIMIT': return 'BUY ORDER';
                            default: return 'BUY STOP';
                          }
                        })()}
                      </Typography>
                    </Box>
                    {orderType === 'MARKET' && (
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontFamily: 'Roboto Mono, monospace',
                          fontWeight: 600,
                          letterSpacing: '1px',
                        }}
                      >
                        {(quote?.depth?.sell?.[0]?.price?.toFixed(2) ||
                          (quote?.ohlc?.close ? quote?.ohlc?.close.toFixed(2) : '0.00'))}
                      </Typography>
                    )}
                  </LoadingButton>
                  {/* SELL Button */}
                  <LoadingButton
                    onClick={handleSubmit((data) => handleTradeSubmit(data, 'SELL'))}
                    data-side="SELL"
                    sx={{
                      ...sharedStyles.actionButton('error'),
                      borderRadius: 0,
                    }}
                    loading={isLoading}
                    disabled={!isValid}
                  ><Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center', width: '100%' }}>
                      <TrendingDown fontSize="small" />
                      <Typography variant="button" sx={{ fontWeight: 600 }}>
                        {(() => {
                          switch (orderType) {
                            case 'MARKET': return 'SELL';
                            case 'LIMIT': return 'SELL ORDER';
                            default: return 'SELL STOP';
                          }
                        })()}
                      </Typography>
                    </Box>
                    {orderType === 'MARKET' && (
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontFamily: 'Roboto Mono, monospace',
                          fontWeight: 600,
                          letterSpacing: '1px',
                        }}
                      >
                        {(quote?.depth?.buy?.[0]?.price?.toFixed(2) ||
                          (quote?.ohlc?.close ? quote?.ohlc?.close.toFixed(2) : '0.00'))}
                      </Typography>
                    )}
                  </LoadingButton>
                </Box>

                {/* Close All Button */}
                {orderType === 'MARKET' && trade.type === 'OPEN' && (<LoadingButton
                  onClick={handleSubmit((data) => {
                    const newData = { ...data, useCloseAll: true };
                    // For "Close All", we need to use the close-trade API
                    if (closeFromPortfolio) {
                      // Special handling when closing from portfolio
                      dispatch(startTradeLoading());
                      try {                        const payload = {
                          ...newData,
                          scriptId: trade.script._id,
                          quantity: trade.quantity,
                          orderType: 'MARKET',
                          // Use the opposite side of the current position
                          side: trade.side === 'BUY' ? 'SELL' : 'BUY',
                          cancelFromPortfolio: true
                        };

                        // Remove unnecessary fields
                        delete payload.limit;
                        delete payload.stopPrice;
                        delete payload.tradeAction;
                        delete payload.useCloseAll;

                        // Close-trade API for closing the entire position
                        axiosInstance.post('/trade/add', payload)
                          .then(response => {
                            enqueueSnackbar(response.data.message);
                            if (response.data.tradesCancelled) {
                              enqueueSnackbar(response.data.cancelMessage, { persist: true });
                            }
                            handleFinalize(true);
                          })
                          .catch(err => {
                            enqueueSnackbar(err.message || 'Error processing your request', { variant: 'error' });
                            handleFinalize(false);
                          });
                      } catch (err) {
                        enqueueSnackbar(err.message || 'Error processing your request', { variant: 'error' });
                        handleFinalize(false);
                      }
                    } else {
                      // Standard close-trade for other contexts
                      dispatch(startTradeLoading());
                      try {
                        const payload = { ...newData };
                        // Use the opposite side of the current position
                        payload.side = trade.side === 'BUY' ? 'SELL' : 'BUY';
                        payload.quantity = trade.quantity;

                        // Close-trade API for closing the entire position
                        axiosInstance.put(`/trade/close-trade/${trade._id}`, payload)
                          .then(response => {
                            enqueueSnackbar(response.data.message);
                            if (response.data.tradesCancelled) {
                              enqueueSnackbar(response.data.cancelMessage, { persist: true });
                            }
                            handleFinalize(true);
                          })
                          .catch(err => {
                            enqueueSnackbar(err.message || 'Error processing your request', { variant: 'error' });
                            handleFinalize(false);
                          });
                      } catch (err) {
                        enqueueSnackbar(err.message || 'Error processing your request', { variant: 'error' });
                        handleFinalize(false);
                      }
                    }
                  })}
                  sx={{
                    height: 54,
                    mt: 1,
                    mb: 1.5,
                    width: '100%',
                    // Use the same vibrant colors as defined in action buttons
                    bgcolor: trade.side === 'BUY' ? '#FF4842' : '#11C15B',
                    color: 'common.white',
                    borderRadius: 6,
                    boxShadow: '0 4px 10px 0 rgba(0,0,0,0.2)',
                    backgroundImage: (theme) => `linear-gradient(45deg, 
                        ${trade.side === 'BUY' ? '#FF4842' : '#11C15B'}, 
                        ${trade.side === 'BUY' ? '#E03730' : '#0DA54D'})`,
                    '&:hover': {
                      bgcolor: trade.side === 'BUY' ? '#C83A34' : '#0CA54C',
                      boxShadow: '0 6px 15px 0 rgba(0,0,0,0.25)',
                      transform: 'translateY(-2px)',
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                      boxShadow: '0 3px 8px 0 rgba(0,0,0,0.15)',
                    },
                    '&.Mui-disabled': {
                      opacity: 0.6,
                      bgcolor: theme => theme.palette.grey[500],
                    },
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontWeight: 700,
                    transition: 'all 0.2s ease-in-out',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 2,
                      bgcolor: 'rgba(255,255,255,0.4)',
                    },
                  }}
                  loading={isLoading}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    {trade.side === 'BUY' ? <TrendingDown fontSize="small" /> : <TrendingUp fontSize="small" />}
                    <Box component="span" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                      CLOSE ALL {trade?.quantity}
                    </Box>
                  </Box>
                </LoadingButton>
                )}

                {/* Quote Information */}
                <Paper
                  elevation={0}
                  sx={{
                    mt: 2,
                    p: 1.5,
                    borderRadius: 1.5,
                    bgcolor: 'background.neutral',
                    border: '1px solid',
                    borderColor: (theme) => alpha(theme.palette.grey[500], 0.12),
                  }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 2,
                      '& > div': {
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                      }
                    }}
                  >
                    <Box>
                      <QuoteValueItem label={'Bid'} value={quote?.depth?.buy?.[0]?.price} />
                      <QuoteValueItem label={'Open'} value={quote?.ohlc?.open} />
                      <QuoteValueItem label={'High'} value={quote?.ohlc?.high} />
                      <QuoteValueItem label={'UC'} value={scriptQuote?.upper_circuit_limit || quote?.upper_circuit_limit} />
                    </Box>
                    <Box>
                      <QuoteValueItem label={'Ask'} value={quote?.depth?.sell?.[0]?.price} />
                      <QuoteValueItem label={'Close'} value={quote?.ohlc?.close} />
                      <QuoteValueItem label={'Low'} value={quote?.ohlc?.low} />
                      <QuoteValueItem label={'LC'} value={scriptQuote?.lower_circuit_limit || quote?.lower_circuit_limit} />
                    </Box>
                  </Box>
                </Paper>
              </Box>
            </FormProvider>
          )}

          {/* PENDING Trade UI */}
          {trade.type === 'PENDING' && (
            <FormProvider methods={methods}>
              <Box sx={{ px: 2, py: 1.5 }}>
                <RHFToggleButtonGroup
                  name="tradeAction"
                  label="Select Action"
                  color="primary"
                  sx={{ width: '100%', mb: 2 }}
                  options={pendingActionOptions}
                  exclusive
                />
                {selectedOption === 'modify' && (
                  <Box sx={{ display: 'grid', gap: 2 }}>
                    <RHFTextField
                      name="quantity"
                      label="QUANTITY"
                      type="number"
                      InputProps={{
                        startAdornment: (
                          <IconButton
                            onMouseDown={decrementHandler}
                            sx={sharedStyles.quantityButton(theme)}
                          >
                            <Box component="span" sx={{ fontSize: '1.25rem', fontWeight: 'bold', lineHeight: 0 }}>−</Box>
                          </IconButton>
                        ),
                        endAdornment: (
                          <IconButton
                            onMouseDown={incrementHandler}
                            sx={sharedStyles.quantityButton(theme)}
                          >
                            <Box component="span" sx={{ fontSize: '1.25rem', fontWeight: 'bold', lineHeight: 0 }}>+</Box>
                          </IconButton>
                        ),
                      }}
                      sx={sharedStyles.numberInput(theme)}
                    />
                    <RHFTextField
                      name="limit"
                      label="PRICE"
                      type="number"
                      InputLabelProps={{ shrink: true }}
                      sx={sharedStyles.numberInput(theme)}
                    />
                  </Box>
                )}
                <Box sx={{ mt: 3 }}>
                  {selectedOption === 'cancel' && (
                    <LoadingButton
                      onClick={handleCancelTrade}
                      fullWidth
                      sx={{
                        ...sharedStyles.actionButton('error'),
                        height: 48,
                        borderRadius: 1,
                        boxShadow: '0 2px 8px 0 rgba(0,0,0,0.15)',
                      }}
                      loading={isLoading}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CloseIcon fontSize="small" />
                        <Typography variant="button" sx={{ fontWeight: 700 }}>
                          Cancel Order
                        </Typography>
                      </Box>
                    </LoadingButton>
                  )}
                  {selectedOption === 'modify' && (
                    <LoadingButton
                      onClick={handleSubmit(handleModifyOrder)}
                      fullWidth
                      sx={{
                        ...sharedStyles.actionButton('primary'),
                        height: 48,
                        borderRadius: 1,
                        boxShadow: '0 2px 8px 0 rgba(0,0,0,0.15)',
                      }}
                      loading={isLoading}
                      disabled={!isValid}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="button" sx={{ fontWeight: 700 }}>
                          Modify Order
                        </Typography>
                      </Box>
                    </LoadingButton>
                  )}
                </Box>
              </Box>
            </FormProvider>
          )}
        </Box>

        {/* Bottom Close Button for Mobile */}
        {isMobile && (
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              left: 0,
              right: 0,
              p: 2,
              pb: 'max(calc(env(safe-area-inset-bottom) + 16px), 16px)',
              bgcolor: 'background.paper',
              borderTop: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 -4px 12px rgba(0,0,0,0.08)',
            }}
          >
            <Button
              onClick={onClose}
              fullWidth
              variant="outlined"
              sx={{
                height: 48,
                borderRadius: 1.5,
                borderColor: (theme) => alpha(theme.palette.grey[500], 0.3),
                color: 'text.primary',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CloseIcon fontSize="small" />
                <Typography variant="button" sx={{ fontWeight: 700 }}>
                  Close
                </Typography>
              </Box>
            </Button>
          </Box>
        )}
      </Box>
    </Wrapper>
  );
};

CloseTradeModal.propTypes = {
  trade: PropTypes.object.isRequired, // Make trade required
  isOpen: PropTypes.bool,
  closeFromPortfolio: PropTypes.bool,
  closeFromTrade: PropTypes.bool,
  onClose: PropTypes.func,
  onUpdateSuccess: PropTypes.func,
  isManualTrade: PropTypes.bool,
};

export default CloseTradeModal;